import { ChangeDetectorRef, Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	CreateLayoutComponent,
	ItemComponent,
	TagComponent,
	StepperProgressComponent,
	IStepProgress,
	AlertComponent,
	ButtonComponent,
	FormGroupComponent,
	SelectComponent,
	TileComponent,
	InputComponent,
	SwitchComponent,
	ButtonIconComponent,
	ToggleComponent,
	TextareaComponent,
	ListDetailComponent,
	DividerComponent,
	LinkComponent,
	MenuComponent,
	IMenu,
	CardSelectComponent,
	InfoGroupComponent,
	EmptyStateComponent,
	ISelect,
	ITagWithLabel,
	ITab,
	TabsComponent,
} from '@tecalisdev/infinity';
import { WorkflowsHelperService } from '../../services/workflows-helper.service';
import { FormGroup, FormControl, ReactiveFormsModule, FormArray, Validators, AbstractControl } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AuthChannel,
	CONFIDENCE_THRESHOLD,
	DEMAND_LEVEL,
	IOperationInfo,
	IPolDemandLevel,
	ITermsAndConditionsForm,
	IWorkflowDetailResponse,
	IErrorsNumbersListResponse,
	IWorkflowsCreatePayload,
	OCR_DOC_COUNTRY_TYPE,
	WorkflowStep,
} from '../../models/workflows.models';
import { LanguageService } from '../../../core/services/language.service';
import { WorkflowFormDefaultsService } from '../../services/workflow-form-defaults.service';
import { WorkflowFormDetailMapperService } from '../../services/workflow-form-detail-mapper.service';
import { WorkflowFormPayloadService } from '../../services/workflow-form-payload.service';
import { WorkflowFormValidationService } from '../../services/workflow-form-validation.service';
import {
	WorkflowFormStepperHost,
	WorkflowFormStepperService,
} from '../../services/workflow-form-stepper.service';
import {
	WorkflowFormCompanyOptionsHost,
	WorkflowFormCompanyOptionsService,
} from '../../services/workflow-form-company-options.service';
import {
	WorkflowFormStepsHost,
	WorkflowFormStepsService,
} from '../../services/workflow-form-steps.service';
import {
	WorkflowFormLocalesHost,
	WorkflowFormLocalesService,
} from '../../services/workflow-form-locales.service';
import {
	WorkflowFormHomeChecksHost,
	WorkflowFormHomeChecksService,
} from '../../services/workflow-form-home-checks.service';
import {
	WorkflowFormDocumentsHost,
	WorkflowFormDocumentsService,
} from '../../services/workflow-form-documents.service';
import { updateDelayedProcessValidationState } from '../../utils/workflow-delayed-process.util';
import {
	DELAYED_WARNING_DEFAULT_TEXT_KEY_BY_LOCALE,
	DELAYED_WARNING_PLACEHOLDER_KEY_BY_LOCALE,
} from '../../constants/workflow-form-defaults.constants';
import {
	allLocalesTextFilledValidator,
	firstMissingLocaleTextId,
	hasWorkflowLocaleText,
	resolveWorkflowLocaleText,
} from '../../utils/workflow-locale-text.util';
import { patchFormStepToEditor } from '../../utils/form-operation-contract.util';
import {
	findDuplicateFinancialConnections,
	isFinancialConnectionDisabled,
} from '../../utils/workflow-connections.util';
import {
	isOcrFraudCheckActiveForPayload,
	isSpainAllowedForDniFromValues,
	resolveOcrAllowedDocsForPayload,
	resolveOcrNotAllowedDocsForPayload,
	shouldClearSkipPolWhenMidni,
} from '../../utils/workflow-ocr.util';
import { buildPolSettingFromForm } from '../../utils/workflow-pol.util';
import { listPrevSelectedDocTypes } from '../../utils/workflow-documents.util';
import {
	areFormControlsValidExcept,
	toggleArrayMembership,
	isWorkflowOperationsSelectionInvalid,
	markPrecedingOperationsCompleted,
	applyOperationsDisabledFromCompleted,
	clearOperationsCompletedFrom,
} from '../../utils/workflow-form-control.util';
import {
	inferLocaleScrollAnchorFromPath,
	localeTextControlPathForOperation,
	operationTypeToLocaleScrollAnchor as mapOperationTypeToLocaleScrollAnchor,
	withSelectedWorkflowLocaleTab,
	WORKFLOW_LOCALE_SCROLL_ANCHOR_IDS,
	WorkflowLocaleScrollAnchor,
} from '../../utils/workflow-locale-scroll.util';
import { maskApiKey } from '../../../core/utils/utils';
import { isSpainCompanyCountry } from '../../utils/company-country.util';
import { normalizeValidationManualErrorIds } from '../../utils/workflow-validation.util';
import { resolveWorkflowMutationErrorMessage } from '../../utils/workflow-api-error.util';
import { isValidationManualRejectedSelected as hasValidationManualRejectedSelected } from '../../utils/workflow-validation-manual-type.util';
import { LoadingService } from '../../../shared/services/loading.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { WorkflowsService } from '../../services/workflows.service';
import { ErrorsNumbersService } from '../../services/errors-numbers.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';
import { MasterService } from '../../../shared/services/master.service';
import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { fraudEmailAlertValidators } from '../../../shared/validators/email.validator';
import { IMasterCompanyElement, MenuItemAction } from '../../../shared/models/master.models';
import { CommonModule } from '@angular/common';
import { ModalOcrFraudScoringComponent } from '../../components/modal-ocr-fraud-scoring/modal-ocr-fraud-scoring.component';
import { ModalFinishResultMessagesComponent } from '../../components/modal-finish-result-messages/modal-finish-result-messages.component';
import { ModalDocFormComponent } from '../../components/modal-doc-form/modal-doc-form.component';
import { ModalWorkflowFormStepComponent } from '../../components/modal-workflow-form-step/modal-workflow-form-step.component';
import {
	FormOcrOrderModalClose,
	FormOcrOrderModalMode,
	ModalWorkflowFormOcrOrderComponent,
} from '../../components/modal-workflow-form-ocr-order/modal-workflow-form-ocr-order.component';
import {
	FORM_OCR_COINCIDENT_FIELD_IDS,
	hasCoincidentFieldWithMatchOcr,
	hasCoincidentFormOcrFields,
	isFormOcrOrderAutofillConfirmed as resolveFormOcrOrderAutofillConfirmed,
	isFormOcrOrderDocumentUi,
	resolveAutofillAfterFormStepsChange,
	shouldPromptFormOcrOrder,
	shouldShowFormOcrOrderSummary,
} from '../../utils/form-ocr-autofill.util';
import {
	findFormFieldPreset,
	inferFormFieldPresetId,
} from '../../components/modal-workflow-form-step/form-field-presets';
import { formStepListTagsSignature, resolveFormStepListTags } from '../../utils/form-step-list-tags.util';
import {
	CONNECTION_TYPE,
	IDocumentsDocs,
	IPolSetting,
	OPERATION_TYPE,
	POL_TEST,
	POL_TYPE,
} from '../../../operations/models/operations.models';
import { ApikeyService } from '../../../settings/pages/apikeys/services/apikey.service';
import { IApikeyListBody, IApikeyListResponse } from '../../../settings/pages/apikeys/models/apikeys.models';
import { ApikeyFormComponent } from '../../../settings/pages/apikeys/pages/apikey-form/apikey-form.component';
import { CompaniesService } from '../../../settings/pages/companies/services/companies.service';
import { ICompaniesDetailResponse } from '../../../settings/pages/companies/models/companies.models';
import { DesignService } from '../../../settings/pages/design/services/design.service';
import { IDesignListBody, IDesignListResponse } from '../../../settings/pages/design/models/design.models';
import { LegalTermsService } from '../../../settings/pages/legal-terms/services/legal-terms.service';
import {
	ILegalTermsListBody,
	ILegalTermsListResponse,
} from '../../../settings/pages/legal-terms/models/legal-terms.models';
import { LegalTermsFormComponent } from '../../../settings/pages/legal-terms/pages/legal-terms-form/legal-terms-form.component';
import { WorkflowReadyComponent } from '../../components/workflow-ready/workflow-ready.component';
import { RetentionDurationInputComponent } from '../../../shared/components/retention-duration-input/retention-duration-input.component';
import { WorkflowFormAttorneyPowersPanelComponent } from './operation-panels/workflow-form-attorney-powers-panel.component';
import { WorkflowFormConnectionsPanelComponent } from './operation-panels/workflow-form-connections-panel.component';
import { WorkflowFormDocumentsPanelComponent } from './operation-panels/workflow-form-documents-panel.component';
import { WorkflowFormFinishPanelComponent } from './operation-panels/workflow-form-finish-panel.component';
import { WorkflowFormFormPanelComponent } from './operation-panels/workflow-form-form-panel.component';
import { WorkflowFormHomePanelComponent } from './operation-panels/workflow-form-home-panel.component';
import { WorkflowFormOcrPanelComponent } from './operation-panels/workflow-form-ocr-panel.component';
import { WorkflowFormOperationPanelHost } from './operation-panels/workflow-form-operation-panel-host';
import { WorkflowFormPolPanelComponent } from './operation-panels/workflow-form-pol-panel.component';
import { WorkflowFormActiveStatusTagComponent } from './workflow-form-active-status-tag.component';
import { WorkflowFormAdvancedCollapseComponent } from './workflow-form-advanced-collapse.component';

type DelayedWarningVisibility = 'start_and_resume' | 'resume_only';
type DelayedWarningMessageType = 'default' | 'custom';

/**
 * Orquestador UI del editor (NIF-530). Mapa: `../../WORKFLOWS_EDITOR.md`.
 *
 * TOC — buscar `// --- NOMBRE ---` (no leer el fichero entero):
 * 1. DI_STATE — injects + estado
 * 2. LIFECYCLE — constructor / ngOnInit
 * 3. OCR_MIDNI — España / MiDNI / skip POL
 * 4. LOCALES — tabs multi-idioma
 * 5. DETAIL_CRUD — hosts, detalle, create/update
 * 6. STEPPER — continue / validación / scroll
 * 7. COMPANY_OPTIONS — company, apikey, theme, 2FA
 * 8. CONNECTIONS — duplicados / disabled
 * 9. OCR_POL_UI — polSetting, tests, fraud modal
 * 10. HOME_CHECKS — policy checks
 * 11. FORM_STEPS — pasos FORM
 * 12. DOCUMENTS — filas docs
 * 13. OPERATIONS_MENU — completed / menú ops
 * 14. DELAYED_PROCESS — aviso diferido
 * 15. LOCALE_SCROLL — focus/scroll locale inválido
 *
 * Servicios: detailMapper / payload / validation / stepper (NIF-845) / company-options / steps / locales.
 */
@Component({
	selector: 'app-workflows-form',
	imports: [
		CreateLayoutComponent,
		ItemComponent,
		TagComponent,
		StepperProgressComponent,
		ReactiveFormsModule,
		TranslatePipe,
		AlertComponent,
		ButtonComponent,
		CommonModule,
		FormGroupComponent,
		SelectComponent,
		TileComponent,
		InputComponent,
		SwitchComponent,
		ButtonIconComponent,
		ToggleComponent,
		TextareaComponent,
		ListDetailComponent,
		DividerComponent,
		LinkComponent,
		MenuComponent,
		CardSelectComponent,
		InfoGroupComponent,
		ModalOcrFraudScoringComponent,
		ModalWorkflowFormOcrOrderComponent,
		ModalFinishResultMessagesComponent,
		EmptyStateComponent,
		ModalDocFormComponent,
		ModalWorkflowFormStepComponent,
		ApikeyFormComponent,
		LegalTermsFormComponent,
		WorkflowReadyComponent,
		TabsComponent,
		RetentionDurationInputComponent,
		WorkflowFormHomePanelComponent,
		WorkflowFormFormPanelComponent,
		WorkflowFormOcrPanelComponent,
		WorkflowFormPolPanelComponent,
		WorkflowFormAttorneyPowersPanelComponent,
		WorkflowFormDocumentsPanelComponent,
		WorkflowFormConnectionsPanelComponent,
		WorkflowFormFinishPanelComponent,
		WorkflowFormActiveStatusTagComponent,
		WorkflowFormAdvancedCollapseComponent,
	],
	templateUrl: './workflows-form.component.html',
	styleUrl: './workflows-form.component.scss',
})
export class WorkflowsFormComponent implements OnInit, WorkflowFormOperationPanelHost {
	// --- DI_STATE ---
	//#region Services
	public reactiveFormService = inject(ReactiveFormsService);
	public workflowsHelper = inject(WorkflowsHelperService);
	public masterService = inject(MasterService);
	private workflowsService = inject(WorkflowsService);
	private loading = inject(LoadingService);
	private router = inject(Router);
	private snackbarService = inject(SnackbarService);
	private modalConfirmationService = inject(ModalConfirmationService);
	private trans = inject(TranslateService);
	private activatedRoute = inject(ActivatedRoute);
	private apikeyService = inject(ApikeyService);
	private companiesService = inject(CompaniesService);
	private errorsNumbersService = inject(ErrorsNumbersService);
	private legalTermsService = inject(LegalTermsService);
	private designService = inject(DesignService);
	private languageService = inject(LanguageService);
	private workflowFormDefaults = inject(WorkflowFormDefaultsService);
	private detailMapper = inject(WorkflowFormDetailMapperService);
	private payloadService = inject(WorkflowFormPayloadService);
	private validationService = inject(WorkflowFormValidationService);
	private stepperService = inject(WorkflowFormStepperService);
	private companyOptionsService = inject(WorkflowFormCompanyOptionsService);
	private formStepsService = inject(WorkflowFormStepsService);
	private localesService = inject(WorkflowFormLocalesService);
	private homeChecksService = inject(WorkflowFormHomeChecksService);
	private documentsService = inject(WorkflowFormDocumentsService);
	private cdr = inject(ChangeDetectorRef);
	private destroyRef = inject(DestroyRef);
	//#endregion

	//#region Variables
	public steps: Array<IStepProgress> = this.workflowsHelper.steps;
	public workflowStep = WorkflowStep;
	public OPERATION_TYPE = OPERATION_TYPE;
	public CONNECTION_TYPE = CONNECTION_TYPE;
	public OCR_DOC_COUNTRY_TYPE = OCR_DOC_COUNTRY_TYPE;
	public DEMAND_LEVEL = DEMAND_LEVEL;
	public POL_TEST = POL_TEST;
	public POL_TYPE = POL_TYPE;
	public CONFIDENCE_THRESHOLD = CONFIDENCE_THRESHOLD;
	public MenuItemAction = MenuItemAction;
	public allowedDevices = this.masterService.allowedDevices;
	public tokenDurationOptions: ISelect[] = this.masterService.expirationOptions;
	public allCountries = this.masterService.countries;
	public demandLevelsPol = this.workflowsHelper.demandLevelsPol;
	public form: FormGroup = this.workflowsHelper.form;
	public operations: IOperationInfo[] = this.workflowsHelper.operations;
	public operationsFiltered = this.operations.filter((opt, i) => opt.show);

	/** Host tipado para paneles de operación (evita castear el componente en plantilla). */
	public get asOperationPanelHost(): WorkflowFormOperationPanelHost {
		return this;
	}

	public showError: string = '';
	public showModalTermsConditions: { mode: MenuItemAction; form?: ITermsAndConditionsForm; index?: number };
	public modalDocForm: { mode: MenuItemAction; prevSelectedDocs?: string[]; form?: any; index?: number };
	public modalFormStep: { mode: MenuItemAction; form?: Record<string, unknown>; index?: number };
	public showModalOcrFraudScoring: { form: FormGroup; show: boolean } = { form: null, show: false };
	/** Modal NIF-657: orden FORM/OCR (autofill vs manual). */
	public showModalFormOcrOrder: { show: boolean; mode: FormOcrOrderModalMode } = {
		show: false,
		mode: 'continue',
	};
	/** Índice OCR pendiente de avanzar tras confirmar el modal NIF-657. */
	public pendingOcrContinueIndex: number | null = null;
	public showModalFinishResultMessages: { form: FormGroup; show: boolean } = { form: null, show: false };
	public id: string;
	public errorDetail: string;
	public showOperationsDetail: boolean = false;
	public selectedPolicyCheckControl: FormControl = new FormControl(null);
	public apikeysOptions: ISelect[];
	public themeOptions: ISelect[];
	public policyChecksOptions: ISelect[] = [];
	/** Lista de opciones para el multiselect de errores de revisión manual. */
	public validationProcessErrorsOptions: ISelect[] = [];
	private errorsNumbersLoaded = false;
	private syncingValidationManualErrors = false;
	public showCreateApikeyForm: boolean = false;
	public companies: ISelect[] = this.masterService.companies;
	public company?: IMasterCompanyElement;
	public workflowReady: boolean = false;
	public workflowData: { id: string; type: 'create' | 'update'; value?: IWorkflowDetailResponse };
	public twoFactorChannelOptions: ISelect[] = [
		{ id: 'none', label: this.trans.instant('WORKFLOWS.FORM.GENERAL.TWO_FACTOR_NONE') },
		{ id: 'whatsapp', label: this.trans.instant('WORKFLOWS.FORM.GENERAL.TWO_FACTOR_WHATSAPP') },
		{ id: 'sms', label: this.trans.instant('WORKFLOWS.FORM.GENERAL.TWO_FACTOR_SMS') },
	];
	/** Pestañas de idioma para textos multi-locale (mismo selector en bloques del formulario). */
	public workflowLocaleTabs: ITab[] = [];
	public selectedWorkflowLocaleId: string = 'es';
	private delayedWarningCustomTextCache: Record<string, string> = {};
	private lastDelayedWarningMessageType: DelayedWarningMessageType = 'default';
	/** Expande bloques colapsados al mostrar error de texto multi-idioma. */
	public expandHomeIndicationsForLocaleScroll = false;
	public expandDocumentsAdvancedForLocaleScroll = false;
	/** Transición elegibilidad DNI España (alta/edición): default MiDNI ON sin pisar carga desde API. */
	private prevSpainAllowedForMidni = false;
	/** Evita que `patchValue` del detalle dispare `syncMidni` con `prev` aún no alineado al payload. */
	private applyingWorkflowDetail = false;
	/** Evita peticiones duplicadas de country al resolver compañía. */
	private resolvingCompanyCountryUuid: string | null = null;
	//#endregion

	// --- LIFECYCLE ---
	constructor() {
		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (modalResponse?.id === 'required-ocr') {
				if (modalResponse?.action) {
					if (modalResponse?.action === 'buttonSecondary') {
						this.form.get(`${WorkflowStep.OPERATIONS}.${OPERATION_TYPE.OCR}.available`).patchValue(true);
					}
					this.showOperationsDetail = true;
				}
				return;
			}
			if (modalResponse?.action === 'buttonPrimary') {
				this.router.navigate(['workflows']);
			}
		});

		this.form.valueChanges.subscribe((value: any) => {
			this.updateDelayedProcessValidation(value);
			this.syncWorkflowLocaleValidators(value?.operations);

			// Operations
			this.operations.forEach((opt: IOperationInfo) => {
				if (opt.type === OPERATION_TYPE.FINISH) {
					opt.show = true;
					return;
				}
				if (value?.operations[opt?.type]) {
					opt.show = value.operations[opt.type].available;
				}
			});
			const previousVisibleTypes = new Set(this.operationsFiltered.map((opt) => opt.type));
			const visibleCount = this.operations.filter((opt) => opt.show).length;
			const hasVisibleSelection = this.operations.some((opt) => opt.show && opt.selected);
			if (!hasVisibleSelection) {
				// p. ej. HOME oculto pero seguía selected: elegir primera visible sin bloquear el resto
				let firstSelectedFound = false;
				this.operations.forEach((opt: IOperationInfo) => {
					if (!opt.show) {
						opt.selected = false;
						return;
					}
					if (!firstSelectedFound) {
						opt.selected = true;
						firstSelectedFound = true;
					} else {
						opt.selected = false;
					}
				});
			} else if (visibleCount !== this.operationsFiltered.length) {
				// Al añadir/quitar ops: mantener una selección visible; el gris lo marca `completed` (NIF-735)
				const selectedVisible = this.operations.find((opt) => opt.show && opt.selected);
				if (!selectedVisible) {
					let firstSelectedFound = false;
					this.operations.forEach((opt: IOperationInfo) => {
						if (opt.show && !firstSelectedFound) {
							opt.selected = true;
							firstSelectedFound = true;
						} else {
							opt.selected = false;
						}
					});
				}
			}
			this.operationsFiltered = this.operations.filter((opt) => opt.show);
			// Op nueva: reiniciar paso a paso desde la primera op (no saltar a la nueva sin datos)
			const firstNewIndex = this.operationsFiltered.findIndex(
				(opt) => opt.type !== OPERATION_TYPE.FINISH && !previousVisibleTypes.has(opt.type),
			);
			if (firstNewIndex > -1) {
				clearOperationsCompletedFrom(this.form, this.operationsFiltered, 0);
				this.operationsFiltered.forEach((opt, index) => {
					opt.selected = index === 0;
				});
			}
			this.syncOperationsDisabledFromCompleted();
			// Actualizamos el formulario
			this.form
				.get('general.antifraud.email_alert')
				.setValidators(fraudEmailAlertValidators(value.general.antifraud.available_email_alert));
			this.form.get('general.antifraud.email_alert').updateValueAndValidity({ emitEvent: false });
			// POL
			this.form
				.get(`operations.${OPERATION_TYPE.POL}.mode`)
				.setValidators(value.operations[OPERATION_TYPE.POL].available ? [Validators.required] : null);
			this.form.get(`operations.${OPERATION_TYPE.POL}.mode`).updateValueAndValidity({ emitEvent: false });
			// Comprobamos si está activado los documentos para setear que se tiene que añadir al menos uno de ellos
			this.form
				.get(`operations.${OPERATION_TYPE.DOCUMENTS}.docs`)
				.setValidators(value.operations[OPERATION_TYPE.DOCUMENTS].available ? [Validators.required] : null);
			this.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.docs`).updateValueAndValidity({ emitEvent: false });
			// Comprobamos si está activado las conexiones para setear que se tiene que añadir al menos uno de ellos
			this.form
				.get(`operations.${OPERATION_TYPE.CONNECTIONS}.connections`)
				.setValidators(value.operations[OPERATION_TYPE.CONNECTIONS].available ? [Validators.required] : null);
			this.form
				.get(`operations.${OPERATION_TYPE.CONNECTIONS}.connections`)
				.updateValueAndValidity({ emitEvent: false });
			this.form
				.get(`operations.${OPERATION_TYPE.CONNECTIONS}.type`)
				.setValidators(value.operations[OPERATION_TYPE.CONNECTIONS].available ? [Validators.required] : null);
			this.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.type`).updateValueAndValidity({ emitEvent: false });
			this.form
				.get(`operations.${OPERATION_TYPE.FORM}.steps`)
				.setValidators(value.operations[OPERATION_TYPE.FORM].available ? [Validators.required] : null);
			this.form.get(`operations.${OPERATION_TYPE.FORM}.steps`).updateValueAndValidity({ emitEvent: false });
			// Operations
			this.checkOperationsSelectionValidity();
		});
	}

	ngOnInit(): void {
		this.ensureTwoFactorWorkflowControl();
		this.buildWorkflowLocaleTabs();
		this.loadErrorsNumbersOptions();
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.refreshStepsForCurrentLang();
			this.buildWorkflowLocaleTabs();
			// Opciones del select KO: title resuelto por Accept-Language en el API.
			this.loadErrorsNumbersOptions(true);
		});
		this.syncWorkflowLocaleValidators(this.form.get('operations')?.value);
		this.id = this.activatedRoute.snapshot.params['id'];
		if (this.masterService.companies?.length === 1) {
			this.form.get('general.company').patchValue(this.masterService.companies[0]);
			this.updateOptionsByCompany();
		}
		const ocr = this.form.get(`operations.${OPERATION_TYPE.OCR}`);
		ocr.get('dni_available')?.valueChanges.subscribe(() => this.syncMidniValidationToggle());
		ocr.get('dni_type')?.valueChanges.subscribe(() => this.syncMidniValidationToggle());
		ocr.get('dni_countries')?.valueChanges.subscribe(() => this.syncMidniValidationToggle());
		ocr.get('midni_validation_enabled')?.valueChanges.subscribe(() => this.syncSkipPolWhenMidniAndPol());
		this.form
			.get(`operations.${OPERATION_TYPE.POL}.available`)
			?.valueChanges.subscribe(() => this.syncSkipPolWhenMidniAndPol());
		if (this.id) {
			this.getDetail();
		} else {
			this.syncMidniValidationToggle();
		}
	}

	public getValidationErrorOptionIds(): string[] {
		return this.validationProcessErrorsOptions.map((option) => String(option.id));
	}

	/**
	 * Solo aplica el default “todos seleccionados” cuando el control está vacío
	 * (alta / API con errors:[] / catálogo recién cargado). No fuerza selección
	 * si el usuario ha usado “deseleccionar todos”.
	 */
	private ensureDefaultValidationManualErrorsSelection(): void {
		if (this.syncingValidationManualErrors) {
			return;
		}

		const control = this.form.get('general.validation_manual_errors');
		if (!control) {
			return;
		}

		const allIds = this.getValidationErrorOptionIds();
		if (allIds.length === 0) {
			return;
		}

		const selectedIds = normalizeValidationManualErrorIds(control.value);
		if (selectedIds.length > 0) {
			return;
		}

		this.syncingValidationManualErrors = true;
		control.patchValue(allIds, { emitEvent: false });
		this.syncingValidationManualErrors = false;
	}

	private loadErrorsNumbersOptions(force = false): void {
		if (this.errorsNumbersLoaded && !force) {
			return;
		}
		this.errorsNumbersLoaded = true;

		this.errorsNumbersService.listAll().subscribe({
			next: (response: IErrorsNumbersListResponse) => {
				const errors = response?.data ?? [];
				const previousIds = normalizeValidationManualErrorIds(
					this.form.get('general.validation_manual_errors')?.value,
				);
				this.validationProcessErrorsOptions = errors.map((e) => ({
					id: e.uuid,
					// Misma copy que catálogo / detalle KO: título resuelto (fallback etiqueta interna).
					label: `${e.number} - ${e.title || e.error}`,
				}));
				// Tras hot-reload de idioma: mantener selección previa; si estaba vacío, default todos.
				if (previousIds.length > 0) {
					const validIds = new Set(this.getValidationErrorOptionIds());
					const kept = previousIds.filter((id) => validIds.has(id));
					this.syncingValidationManualErrors = true;
					this.form
						.get('general.validation_manual_errors')
						?.patchValue(kept, { emitEvent: false });
					this.syncingValidationManualErrors = false;
				} else {
					this.ensureDefaultValidationManualErrorsSelection();
				}
				this.cdr.markForCheck();
			},
			error: () => {
				this.validationProcessErrorsOptions = [];
			},
		});
	}

	/** Recalcula labels del stepper (se asignan una vez al crear el componente). */
	private refreshStepsForCurrentLang(): void {
		const selectedId = this.steps.find((step) => step.selected)?.id;
		this.steps = this.workflowsHelper.steps.map((step) => ({
			...step,
			selected: step.id === selectedId,
		}));
	}

	// --- OCR_MIDNI ---
	/** True si la multi-selección de validación manual incluye rechazados (muestra selector de errores). */
	public isValidationManualRejectedSelected(raw: unknown): boolean {
		return hasValidationManualRejectedSelected(raw);
	}

	/** Compañía del workflow con country España (ISO esp / ESP desde master). */
	public isCompanyCountrySpain(): boolean {
		return isSpainCompanyCountry(this.company?.country);
	}

	/** Nombre localizado del país de la compañía (para copy Schengen). */
	public getCompanyCountryName(): string {
		const code = this.company?.country;
		if (!code) {
			return '';
		}
		const key = `MASTER.COUNTRIES.${code.toUpperCase()}`;
		const label = this.trans.instant(key);
		return label !== key ? label : code;
	}

	/** España permitida para DNI según tipo de listado (códigos ISO en minúsculas, ej. esp). */
	public isSpainAllowedForDni(): boolean {
		return isSpainAllowedForDniFromValues(
			Boolean(this.form.get(`operations.${OPERATION_TYPE.OCR}.dni_available`)?.value),
			this.form.get(`operations.${OPERATION_TYPE.OCR}.dni_type`)?.value,
			this.form.get(`operations.${OPERATION_TYPE.OCR}.dni_countries`)?.value,
		);
	}

	private syncMidniValidationToggle(): void {
		if (this.applyingWorkflowDetail) {
			return;
		}
		const spain = this.isSpainAllowedForDni();
		if (!spain) {
			this.form
				.get(`operations.${OPERATION_TYPE.OCR}.midni_validation_enabled`)
				?.patchValue(false, { emitEvent: false });
			this.prevSpainAllowedForMidni = false;
			this.syncSkipPolWhenMidniAndPol();
			return;
		}
		if (!this.prevSpainAllowedForMidni) {
			this.form
				.get(`operations.${OPERATION_TYPE.OCR}.midni_validation_enabled`)
				?.patchValue(true, { emitEvent: false });
		}
		this.prevSpainAllowedForMidni = spain;
		this.syncSkipPolWhenMidniAndPol();
	}

	/** Si no hay POL, MiDNI desactivado o DNI España no aplica, no se puede saltar POL. */
	private syncSkipPolWhenMidniAndPol(): void {
		const polOn = this.form.get(`operations.${OPERATION_TYPE.POL}.available`)?.value === true;
		const midniOn = this.form.get(`operations.${OPERATION_TYPE.OCR}.midni_validation_enabled`)?.value === true;
		if (shouldClearSkipPolWhenMidni(polOn, midniOn, this.isSpainAllowedForDni())) {
			this.form
				.get(`operations.${OPERATION_TYPE.OCR}.skip_pol_when_midni_authenticated`)
				?.patchValue(false, { emitEvent: false });
		}
	}

	// --- LOCALES ---
	private asLocalesHost(): WorkflowFormLocalesHost {
		return this as unknown as WorkflowFormLocalesHost;
	}

	/** Construye pestañas de idioma alineadas con LanguageService.getAllowedLanguages(). */
	public buildWorkflowLocaleTabs(): void {
		this.localesService.buildWorkflowLocaleTabs(this.asLocalesHost());
	}

	public onWorkflowLocaleTabSelected(tab: { id: string; label: string }): void {
		this.localesService.onWorkflowLocaleTabSelected(this.asLocalesHost(), tab);
	}

	/**
	 * Devuelve el FormControl del locale activo. Evita [formControlName] dinámico, que no desvincula bien al cambiar de pestaña.
	 */
	public getWorkflowLocaleControl(...segments: string[]): FormControl {
		return this.localesService.getWorkflowLocaleControl(this.asLocalesHost(), ...segments);
	}

	/** Título de fila en lista de documentos (mapa o string legacy). */
	public resolveDocRowTitle(value: unknown): string {
		return this.localesService.resolveDocRowTitle(value);
	}

	/** Pauta de un paso FORM en listado (mapa o string legacy). */
	public resolveFormStepListTitle(stepIndex: number): string {
		return this.localesService.resolveFormStepListTitle(this.asLocalesHost(), stepIndex);
	}

	private getToggleValue<T extends string>(value: unknown, fallback: T): T {
		return this.detailMapper.getToggleValue(value, fallback);
	}

	private getDelayedWarningDefaultTextMap(locales: readonly string[]): Record<string, string> {
		return this.detailMapper.getDelayedWarningDefaultTextMap(locales);
	}

	private getDelayedWarningEmptyTextMap(locales: readonly string[]): Record<string, string> {
		return this.detailMapper.getDelayedWarningEmptyTextMap(locales);
	}

	public getDelayedWarningPlaceholder(): string {
		const warningTextType = this.getToggleValue<DelayedWarningMessageType>(
			this.form.get(`general.delayed_process_warning_text_type`)?.value,
			'default',
		);
		const locale = this.selectedWorkflowLocaleId || 'es';
		const placeholderKey =
			DELAYED_WARNING_PLACEHOLDER_KEY_BY_LOCALE[locale] || 'WORKFLOWS.FORM.GENERAL.ALERT_POSTERGADO_PLACEHOLDER';
		const defaultTextKey =
			DELAYED_WARNING_DEFAULT_TEXT_KEY_BY_LOCALE[locale] ||
			'WORKFLOWS.FORM.GENERAL.ALERT_POSTERGADO_DEFAULT_TEXT';

		if (warningTextType === 'custom') {
			return this.trans.instant(placeholderKey);
		}

		return this.trans.instant(defaultTextKey);
	}

	// --- DETAIL_CRUD ---
	private asDetailHost() {
		return this as unknown as import('../../services/workflow-form-detail-mapper.service').WorkflowFormDetailHost;
	}

	private asPayloadHost() {
		return this as unknown as import('../../services/workflow-form-payload.service').WorkflowFormPayloadHost;
	}

	private asValidationHost() {
		return {
			form: this.form,
			operationsFiltered: this.operationsFiltered,
			formIsValidExcept: (form, field) => this.formIsValidExcept(form, field),
			onOperationInvalid: (index, type) => this.selectAndScrollOperation(index, type),
		};
	}

	private getDetail(): void {
		this.errorDetail = '';
		this.loading.show(this.trans.instant('LOADING.GET_WORKFLOWS'));
		this.workflowsService.detail(this.id).subscribe({
			next: (response: IWorkflowDetailResponse) => {
				this.form.get('general.has_saved_state').patchValue(null);
				this.form.get('general.validation').patchValue(null);
				this.setFormData(response);
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.errorDetail = error.error.message || this.trans.instant('WORKFLOWS.FORM.DETAIL_ERROR_DESCRIPTION');
			},
			complete: () => {},
		});
	}

	private setFormData(detail: IWorkflowDetailResponse): void {
		const company = detail?.company_uuid;
		this.form.get('general.company').patchValue(company);
		// Si solo hay una compañía, se selecciona automáticamente
		if (this.masterService.companies?.length === 1) {
			this.form.get('general.company').patchValue(this.masterService.companies[0]);
		}
		this.updateOptionsByCompany();
		this.loading.hide();
		this.detailMapper.applyDetail(detail, this.asDetailHost());
		this.ensureDefaultValidationManualErrorsSelection();
		// Edición: ops ya configuradas → no gris en el menú lateral (NIF-735)
		this.markAllVisibleOperationsCompleted();
		this.syncOperationsDisabledFromCompleted();
	}

	private create(): void {
		this.loading.show(this.trans.instant('LOADING.CREATE_WORKFLOWS'));
		this.showError = '';
		const body: IWorkflowsCreatePayload = this.getBody();

		this.workflowsService.create(body).subscribe({
			next: (response) => {
				this.workflowReady = true;
				const value: IWorkflowDetailResponse = { ...body, uuid: response.profile_uuid } as any;
				this.workflowData = { id: response.profile_uuid, type: 'create', value };
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.showError = resolveWorkflowMutationErrorMessage(
					error,
					this.trans.instant('WORKFLOWS.FORM.CREATE_ERROR'),
					this.trans.instant('WORKFLOWS.FORM.ERROR_DESIGN_COMPANY'),
				);
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	private update(): void {
		this.loading.show(this.trans.instant('LOADING.UPDATE_WORKFLOWS'));
		this.showError = '';
		const body: IWorkflowsCreatePayload = this.getBody();

		this.workflowsService.update(this.id, body).subscribe({
			next: (response: IWorkflowDetailResponse) => {
				this.workflowReady = true;
				this.workflowData = { id: response.uuid, type: 'update', value: { ...body, ...response } };
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.showError = resolveWorkflowMutationErrorMessage(
					error,
					this.trans.instant('WORKFLOWS.FORM.UPDATE_ERROR'),
					this.trans.instant('WORKFLOWS.FORM.ERROR_DESIGN_COMPANY'),
				);
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	// --- STEPPER ---
	private asStepperHost(): WorkflowFormStepperHost {
		return this as unknown as WorkflowFormStepperHost;
	}

	public onContinue(): void {
		this.stepperService.onContinue(this.asStepperHost());
	}

	public cancel(): void {
		this.stepperService.cancel(this.asStepperHost());
	}

	public nextOperation(index: number): void {
		this.stepperService.nextOperation(this.asStepperHost(), index);
	}

	public globalContinue(): void {
		this.stepperService.globalContinue(this.asStepperHost());
	}

	private tryHandleFormOcrOrderPrompt(opIndex: number): boolean {
		return this.stepperService.tryHandleFormOcrOrderPrompt(this.asStepperHost(), opIndex);
	}

	public onFormOcrOrderModalClose(event: FormOcrOrderModalClose): void {
		this.stepperService.onFormOcrOrderModalClose(this.asStepperHost(), event);
	}

	public openFormOcrOrderEdit(): void {
		this.stepperService.openFormOcrOrderEdit(this.asStepperHost());
	}

	public shouldShowFormOcrOrderItem(): boolean {
		return this.stepperService.shouldShowFormOcrOrderItem(this.asStepperHost());
	}

	public isFormOcrOrderAutofill(): boolean {
		return this.stepperService.isFormOcrOrderAutofill(this.asStepperHost());
	}

	public isFormOcrOrderAutofillConfirmed(): boolean {
		return this.stepperService.isFormOcrOrderAutofillConfirmed(this.asStepperHost());
	}

	private clearMatchOcrOnCoincidentFormFields(): void {
		this.stepperService.clearMatchOcrOnCoincidentFormFields(this.asStepperHost());
	}

	private selectAndScrollToFormOperation(): void {
		this.stepperService.selectAndScrollToFormOperation(this.asStepperHost());
	}

	private markPrecedingOperationsCompleted(targetIndex: number): void {
		this.stepperService.markPrecedingOperationsCompleted(this.asStepperHost(), targetIndex);
	}

	private markAllVisibleOperationsCompleted(): void {
		this.stepperService.markAllVisibleOperationsCompleted(this.asStepperHost());
	}

	private syncOperationsDisabledFromCompleted(): void {
		this.stepperService.syncOperationsDisabledFromCompleted(this.asStepperHost());
	}

	private areOperationsValid(i: number): boolean {
		return this.stepperService.areOperationsValid(this.asStepperHost(), i);
	}

	private selectAndScrollOperation(index: number, type: string): void {
		this.stepperService.selectAndScrollOperation(this.asStepperHost(), index, type);
	}

	private findFirstInvalidControlPath(control: AbstractControl): string[] | null {
		return this.stepperService.findFirstInvalidControlPath(control);
	}

	// --- COMPANY_OPTIONS ---
	private asCompanyOptionsHost(): WorkflowFormCompanyOptionsHost {
		return this as unknown as WorkflowFormCompanyOptionsHost;
	}

	public closeApikeyForm(reload: boolean): void {
		this.companyOptionsService.closeApikeyForm(this.asCompanyOptionsHost(), reload);
	}

	public createTheme(): void {
		this.companyOptionsService.createTheme();
	}

	private updatePolicyChecksOptions(): void {
		this.companyOptionsService.updatePolicyChecksOptions(this.asCompanyOptionsHost());
	}

	public addPolicyCheck(policyCheckUuid: string | any): void {
		this.companyOptionsService.addPolicyCheck(this.asCompanyOptionsHost(), policyCheckUuid);
	}

	private getBody(): IWorkflowsCreatePayload {
		return this.payloadService.buildCreatePayload(this.asPayloadHost());
	}

	public retentionHasError(control: AbstractControl | null): boolean {
		return !!control && control.touched && control.invalid;
	}

	private ensureTwoFactorWorkflowControl(): void {
		this.companyOptionsService.ensureTwoFactorWorkflowControl(this.form);
	}

	public isTwoFactorWorkflowEnabled(): boolean {
		return this.companyOptionsService.isTwoFactorWorkflowEnabled(this.form);
	}

	private getSelectedTwoFactorWorkflowChannel(): AuthChannel {
		return this.companyOptionsService.getSelectedTwoFactorWorkflowChannel(this.form);
	}

	// --- CONNECTIONS ---
	public getDuplicateConnection(): IDocumentsDocs[] {
		return findDuplicateFinancialConnections(this.docsAvailables, this.connectionsAvailables);
	}

	public isConnectionDisabled(type: CONNECTION_TYPE): boolean {
		return isFinancialConnectionDisabled(type, this.docsAvailables, this.connectionsAvailables);
	}

	private get docsAvailables(): IDocumentsDocs[] {
		return this.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.available`).value
			? this.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.docs`)?.value
			: [];
	}

	private get connectionsAvailables(): CONNECTION_TYPE[] {
		return this.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.available`)?.value
			? this.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.connections`)?.value
			: [];
	}

	/** Continúa COMPANY_OPTIONS tras helpers CONNECTIONS (orden legacy del fichero). */
	public updateOptionsByCompany(): void {
		this.companyOptionsService.updateOptionsByCompany(this.asCompanyOptionsHost());
	}

	private updateCompanySelected(): void {
		this.companyOptionsService.updateCompanySelected(this.asCompanyOptionsHost());
	}

	private ensureCompanyCountryResolved(companyUuid?: string): void {
		this.companyOptionsService.ensureCompanyCountryResolved(this.asCompanyOptionsHost(), companyUuid);
	}

	private syncSchengenUnlimitedForCompany(): void {
		this.companyOptionsService.syncSchengenUnlimitedForCompany(this.asCompanyOptionsHost());
	}

	private applyWorkflowValidationForCompany(): void {
		this.companyOptionsService.applyWorkflowValidationForCompany(this.asCompanyOptionsHost());
	}

	private updateApikeysOptions(): void {
		this.companyOptionsService.updateApikeysOptions(this.asCompanyOptionsHost());
	}

	private updateThemeOptions(): void {
		this.companyOptionsService.updateThemeOptions(this.asCompanyOptionsHost());
	}

	public moveToOperation(operation: IOperationInfo): void {
		this.stepperService.moveToOperation(this.asStepperHost(), operation);
	}

	public changeOperationSelected(operation: IOperationInfo): void {
		this.stepperService.changeOperationSelected(this.asStepperHost(), operation);
	}

	private availableCheckFraud(type: string): boolean {
		return isOcrFraudCheckActiveForPayload(
			this.form.get(`operations.${OPERATION_TYPE.OCR}.fraud_scoring.type`)?.value,
			Boolean(this.form.get(`operations.${OPERATION_TYPE.OCR}.fraud_scoring.${type}.available`)?.value),
		);
	}

	private getAllowedDocs(doc: 'dni' | 'passport'): Array<string> | null {
		return resolveOcrAllowedDocsForPayload(
			Boolean(this.form.get(`operations.${OPERATION_TYPE.OCR}.${doc}_available`)?.value),
			this.form.get(`operations.${OPERATION_TYPE.OCR}.${doc}_type`)?.value,
			this.form.get(`operations.${OPERATION_TYPE.OCR}.${doc}_countries`)?.value,
		) as Array<string> | null;
	}

	// --- OCR_POL_UI ---
	get polSetting(): IPolSetting {
		return buildPolSettingFromForm(
			{
				demandType: this.form.get(`operations.${OPERATION_TYPE.POL}.type`)?.value,
				totalTries: this.form.get(`operations.${OPERATION_TYPE.POL}.setting.total_tries`)?.value,
				settingType: this.form.get(`operations.${OPERATION_TYPE.POL}.setting.type`)?.value,
				totalTest: this.form.get(`operations.${OPERATION_TYPE.POL}.setting.total_test`)?.value,
				scoringMode: this.form.get(`operations.${OPERATION_TYPE.POL}.setting.scoring`)?.value,
				scoringCustom: this.form.get(`operations.${OPERATION_TYPE.POL}.setting.scoring_custom`)?.value,
				availableTests: this.form.get(`operations.${OPERATION_TYPE.POL}.setting.available_tests`)?.value,
				maxTime: this.form.get(`operations.${OPERATION_TYPE.POL}.setting.max_time`)?.value,
			},
			this.demandLevelsPol,
		);
	}

	private getNotAllowedDocs(doc: 'dni' | 'passport'): any {
		return resolveOcrNotAllowedDocsForPayload(
			Boolean(this.form.get(`operations.${OPERATION_TYPE.OCR}.${doc}_available`)?.value),
			this.form.get(`operations.${OPERATION_TYPE.OCR}.${doc}_type`)?.value,
			this.form.get(`operations.${OPERATION_TYPE.OCR}.${doc}_countries`)?.value,
		);
	}

	public changeAvailableTests(status: boolean, test: POL_TEST): void {
		const control = this.form.get(`operations.${OPERATION_TYPE.POL}.setting.available_tests`);
		const next = toggleArrayMembership(control?.value, test, status);
		if (next !== control?.value) {
			control?.setValue(next);
		}
	}

	public changeAvailableFinancialEvaluation(status: boolean, connection: CONNECTION_TYPE): void {
		const control = this.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.connections`);
		const next = toggleArrayMembership(control?.value, connection, status);
		if (next !== control?.value) {
			control?.setValue(next);
		}
	}
	public saveOcrFraud(result: { form: FormGroup; show: boolean }): void {
		if (result.form) {
			this.form.get(`operations.${OPERATION_TYPE.OCR}.fraud_scoring`).setValue(result.form.getRawValue());
		}
		this.showModalOcrFraudScoring.show = false;
	}

	get ocrFraudScoring(): FormGroup {
		let newForm: FormGroup = this.workflowsHelper.formOcrFraudScoring;
		newForm.patchValue(this.form.get(`operations.${OPERATION_TYPE.OCR}.fraud_scoring`).getRawValue());
		return newForm;
	}

	public openFinishResultMessagesModal(): void {
		this.showModalFinishResultMessages = { show: true, form: this.finishResultMessagesDraft };
	}

	get finishResultMessagesDraft(): FormGroup {
		const draft = this.workflowsHelper.buildFinishResultMessagesFormGroup();
		const finishGroup = this.form.get(`operations.${OPERATION_TYPE.FINISH}`) as FormGroup;
		const fields = [
			'loading_text',
			'ok_title',
			'ok_description',
			'ko_title',
			'ko_description',
			'cancelled_title',
			'cancelled_description',
			'pending_title',
			'pending_description',
		] as const;
		for (const field of fields) {
			const source = finishGroup.get(field)?.getRawValue();
			if (source) {
				draft.get(field)?.patchValue(source);
			}
		}
		return draft;
	}

	public saveFinishResultMessages(result: { form: FormGroup | null; show: boolean }): void {
		if (result.form) {
			const finishGroup = this.form.get(`operations.${OPERATION_TYPE.FINISH}`) as FormGroup;
			const raw = result.form.getRawValue();
			for (const field of Object.keys(raw)) {
				finishGroup.get(field)?.patchValue(raw[field]);
			}
		}
		this.showModalFinishResultMessages = { form: null, show: false };
	}

	// --- HOME_CHECKS ---
	//#region HOME CHECKS
	private asHomeChecksHost(): WorkflowFormHomeChecksHost {
		return this as unknown as WorkflowFormHomeChecksHost;
	}

	get checksArray(): FormArray {
		return this.form.get(`operations.${OPERATION_TYPE.HOME}.policy_checks`) as FormArray;
	}

	public removeCheck(index: number): void {
		this.homeChecksService.removeCheck(this.asHomeChecksHost(), index);
	}

	public formCheck(response: { mode: MenuItemAction; form?: ITermsAndConditionsForm; index?: number }): void {
		this.homeChecksService.formCheck(this.asHomeChecksHost(), response);
	}

	public createPolicyCheck(reload: boolean): void {
		this.homeChecksService.createPolicyCheck(this.asHomeChecksHost(), reload);
	}

	public policyCheckMenuChange(action: IMenu, index: number): void {
		this.homeChecksService.policyCheckMenuChange(this.asHomeChecksHost(), action, index);
	}

	public tecalisPolicyMenuChange(action: IMenu): void {
		this.homeChecksService.tecalisPolicyMenuChange(this.asHomeChecksHost(), action);
	}

	//#endregion

	// --- FORM_STEPS ---
	//#region FORM STEPS
	private asFormStepsHost(): WorkflowFormStepsHost {
		return this as unknown as WorkflowFormStepsHost;
	}

	get formStepsArray(): FormArray {
		return this.form.get(`operations.${OPERATION_TYPE.FORM}.steps`) as FormArray;
	}

	public removeFormStep(index: number): void {
		this.formStepsService.removeFormStep(this.asFormStepsHost(), index);
	}

	public formStep(info: { form: Record<string, unknown>; index?: number } | null): void {
		this.formStepsService.formStep(this.asFormStepsHost(), info);
	}

	private syncFormOcrOrderPreferenceAfterFormChange(hadCotejo: boolean): void {
		this.formStepsService.syncFormOcrOrderPreferenceAfterFormChange(this.asFormStepsHost(), hadCotejo);
	}

	private patchFormStepGroup(stepGroup: FormGroup, raw: Record<string, unknown>): void {
		this.formStepsService.patchFormStepGroup(this.asFormStepsHost(), stepGroup, raw);
	}

	public newFormStep(): void {
		this.formStepsService.newFormStep(this.asFormStepsHost());
	}

	public formStepsMenuChange(action: IMenu, index: number): void {
		this.formStepsService.formStepsMenuChange(this.asFormStepsHost(), action, index);
	}

	public onFormStepMove(direction: 'up' | 'down', index: number): void {
		this.formStepsService.onFormStepMove(this.asFormStepsHost(), direction, index);
	}

	public moveFormStep(index: number, direction: -1 | 1): void {
		this.formStepsService.moveFormStep(this.asFormStepsHost(), index, direction);
	}

	/** Nombre del tipo de paso (preset del picker), p. ej. «Nombre», «Dirección» (Figma `8980:251433`). */
	public resolveFormStepPresetTypeLabel(stepIndex: number): string {
		return this.formStepsService.resolveFormStepPresetTypeLabel(this.asFormStepsHost(), stepIndex);
	}

	public formStepListTagsCache = new Map<number, { signature: string; tags: ITagWithLabel[] }>();

	private invalidateFormStepFieldTagsCache(): void {
		this.formStepsService.invalidateFormStepFieldTagsCache(this.asFormStepsHost());
	}

	/** Etiquetas del listado de pasos FORM: solo Obligatorio y Cotejar (Figma `9238:44360`). */
	public getFormStepListTags(stepIndex: number): ITagWithLabel[] {
		return this.formStepsService.getFormStepListTags(this.asFormStepsHost(), stepIndex);
	}

	private renumberFormSteps(): void {
		this.formStepsService.renumberFormSteps(this.asFormStepsHost());
	}
	//#endregion

	// --- DOCUMENTS ---
	//#region DOCS
	private asDocumentsHost(): WorkflowFormDocumentsHost {
		return this as unknown as WorkflowFormDocumentsHost;
	}

	get documentsArray(): FormArray {
		return this.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.docs`) as FormArray;
	}

	public removeDoc(index: number): void {
		this.documentsService.removeDoc(this.asDocumentsHost(), index);
	}

	public formDoc(info: { form: any; index?: number }): void {
		this.documentsService.formDoc(this.asDocumentsHost(), info);
	}

	public newDoc(): void {
		this.documentsService.newDoc(this.asDocumentsHost());
	}

	public documentsMenuChange(action: IMenu, index: number): void {
		this.documentsService.documentsMenuChange(this.asDocumentsHost(), action, index);
	}

	private prevSelectedDocs(index?: number): string[] {
		return this.documentsService.prevSelectedDocs(this.asDocumentsHost(), index);
	}
	//#endregion

	// --- OPERATIONS_MENU ---
	public formIsValidExcept(form: Partial<FormGroup>, field: string): boolean {
		return this.stepperService.formIsValidExcept(form, field);
	}

	public operationMenuChange(action: IMenu, op: IOperationInfo): void {
		this.stepperService.operationMenuChange(this.asStepperHost(), action, op);
	}

	private isOperationsSelectionInvalid(): boolean {
		return this.stepperService.isOperationsSelectionInvalid(this.asStepperHost());
	}

	private checkOperationsSelectionValidity(): boolean {
		return this.stepperService.checkOperationsSelectionValidity(this.asStepperHost());
	}

	// --- DELAYED_PROCESS ---
	private updateDelayedProcessValidation(value: any): void {
		const warningTextType = this.getToggleValue<DelayedWarningMessageType>(
			value?.general?.delayed_process_warning_text_type,
			'default',
		);
		const delayedTextGroup = this.form.get('general.delayed_process_warning_text') as FormGroup;
		const allowedLanguages = this.languageService.getAllowedLanguages();
		const next = updateDelayedProcessValidationState({
			warningTextType,
			delayedTextGroup,
			allowedLanguages,
			applyingWorkflowDetail: this.applyingWorkflowDetail,
			delayedWarningCustomTextCache: this.delayedWarningCustomTextCache,
			lastDelayedWarningMessageType: this.lastDelayedWarningMessageType,
			getDelayedWarningDefaultTextMap: (locales) => this.getDelayedWarningDefaultTextMap(locales),
			getDelayedWarningEmptyTextMap: (locales) => this.getDelayedWarningEmptyTextMap(locales),
		});
		this.delayedWarningCustomTextCache = next.delayedWarningCustomTextCache;
		this.lastDelayedWarningMessageType = next.lastDelayedWarningMessageType;
	}

	/** Textos multi-idioma obligatorios en todos los locales cuando la operación está activa. */
	private syncWorkflowLocaleValidators(
		operations: Record<string, { available?: boolean; custom_title?: boolean }> | undefined,
	): void {
		if (!operations) {
			return;
		}
		this.workflowsHelper.setLocaleTextGroupRequired(
			this.form.get(`operations.${OPERATION_TYPE.HOME}.description`) as FormGroup,
			Boolean(operations[OPERATION_TYPE.HOME]?.available),
		);
		this.workflowsHelper.setLocaleTextGroupRequired(
			this.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.title`) as FormGroup,
			Boolean(operations[OPERATION_TYPE.DOCUMENTS]?.available) &&
				Boolean(operations[OPERATION_TYPE.DOCUMENTS]?.custom_title),
		);
		// FINISH mensajes: vacío = defaults Identity en send; no exigir required (NIF-762).
		// Si hay valor parcial, el modal «Mensajes de resultado» bloquea al guardar el borrador.
		for (const field of [
			'loading_text',
			'ok_title',
			'ko_title',
			'cancelled_title',
			'pending_title',
		] as const) {
			this.workflowsHelper.setLocaleTextGroupRequired(
				this.form.get(`operations.${OPERATION_TYPE.FINISH}.${field}`) as FormGroup,
				false,
			);
		}
	}

	// --- LOCALE_SCROLL ---
	/** Activa la pestaña del idioma con el primer error de texto multi-idioma. */
	private selectWorkflowLocaleTab(localeId: string): void {
		this.localesService.selectWorkflowLocaleTab(this.asLocalesHost(), localeId);
	}

	private getLocaleGroupForOperation(operationType: string): FormGroup | null {
		return this.localesService.getLocaleGroupForOperation(this.asLocalesHost(), operationType);
	}

	private operationTypeToLocaleScrollAnchor(operationType: string): WorkflowLocaleScrollAnchor | null {
		return mapOperationTypeToLocaleScrollAnchor(operationType);
	}

	private inferScrollContextFromPath(stepId: string, path: string[]): WorkflowLocaleScrollAnchor | null {
		return inferLocaleScrollAnchorFromPath(stepId, path);
	}

	/** Expande bloques necesarios, hace scroll al ancla y enfoca el control. */
	private scheduleScrollToWorkflowLocaleAnchor(anchor: WorkflowLocaleScrollAnchor): void {
		this.localesService.scheduleScrollToWorkflowLocaleAnchor(this.asLocalesHost(), anchor);
	}

	private focusWorkflowLocaleForOperation(operationType: string): boolean {
		return this.localesService.focusWorkflowLocaleForOperation(this.asLocalesHost(), operationType);
	}

	private focusWorkflowLocaleOnInvalidStep(stepId: string): boolean {
		return this.localesService.focusWorkflowLocaleOnInvalidStep(this.asLocalesHost(), stepId);
	}
}
